import { useState } from 'react';
import { useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import {
  DisplayFormattedDate,
  EmptyIndicator,
  MoreOptions,
  Pagination,
  User
} from 'components/common';
import {
  Box,
  BoxOverflowWrapper,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow,
  BoxTitleWithSelectedItems
} from 'components/common/box';
import {
  CalendarEventParticipantsAttributes,
  CalendarEventParticipantsEditCustomAttributes,
  CalendarEventParticipantsPromoteFromQueue,
  CalendarEventParticipantsQueuedHeader,
  CalendarEventParticipantsRemove
} from '.';

import type { GetCalendarEventUsersRes } from 'shared-types/responses';

interface Props {
  updateAddedUsers: () => Promise<void>;
}

const CalendarEventParticipantsQueued = ({ updateAddedUsers }: Props) => {
  const {
    messages,
    pageState: { calendarEvent }
  } = useReduxState();

  const [pageValue, setPageValue] = useState(1);
  const [search, setSearch] = useState<string>('');

  const { data, loading, updateData } = useApiData<GetCalendarEventUsersRes>({
    url:
      `/admin/calendar-events/${calendarEvent.id}/users?status=queued` +
      `&page=${pageValue}` +
      `&search=${search}` +
      '&sortBy=added'
  });

  const handleSearch = ({ search: searchString }) => {
    setSearch(searchString);
    setPageValue(1);
  };

  const updateAllUsers = async () => {
    await updateData();
    await updateAddedUsers();
  };

  return (
    <div>
      <Box
        headerContent={
          <CalendarEventParticipantsQueuedHeader
            allUserIds={data?.allUserIds}
            matchingUserIds={data?.allMatchingUserIds}
            onSearch={handleSearch}
            updateUsers={updateAllUsers}
          />
        }
        headerContentWrap={true}
        loading={loading}
        padding={0}
        title={
          <BoxTitleWithSelectedItems
            category="warning-light"
            numberOfItems={calendarEvent.numberOfParticipantsQueued || 0}
            title={messages.eventCalendarQueued}
          />
        }
      >
        {data?.users?.length ? (
          <BoxOverflowWrapper>
            <BoxTable>
              <BoxTableHeader
                withCheckbox={true}
                withCheckboxIdentifier="participants-queued"
              >
                <BoxTableCell>{messages.commonUser}</BoxTableCell>

                <BoxTableCell>{messages.commonAdded}</BoxTableCell>

                <BoxTableCell>
                  {messages.calendarEventPositionInQueue}
                </BoxTableCell>

                {calendarEvent.userAttributes?.length ? (
                  <BoxTableCell>
                    {messages.eventCalendarParticipantData}
                  </BoxTableCell>
                ) : null}

                <BoxTableCell shrink={true} />
              </BoxTableHeader>

              {data.users.map((user, index) => (
                <BoxTableRow key={user.id} withCheckboxValue={user.id}>
                  <BoxTableCell>
                    <User enableProfileLink={true} user={user} />
                  </BoxTableCell>

                  <BoxTableCell>
                    <DisplayFormattedDate date={user.added} />
                  </BoxTableCell>

                  <BoxTableCell>{index + 1}</BoxTableCell>

                  {calendarEvent.userAttributes?.length ? (
                    <BoxTableCell>
                      <CalendarEventParticipantsAttributes
                        user={user}
                        userAttributes={user.userAttributes}
                      />
                    </BoxTableCell>
                  ) : null}

                  <BoxTableCell>
                    <MoreOptions
                      renderAsFixed={true}
                      styleContentButtons={false}
                    >
                      <CalendarEventParticipantsEditCustomAttributes
                        updateUsers={updateAllUsers}
                        user={user}
                      />

                      <CalendarEventParticipantsPromoteFromQueue
                        updateUsers={updateAllUsers}
                        user={user}
                      />
                      <CalendarEventParticipantsRemove
                        status="queued"
                        updateUsers={updateAllUsers}
                        user={user}
                      />
                    </MoreOptions>
                  </BoxTableCell>
                </BoxTableRow>
              ))}
            </BoxTable>
          </BoxOverflowWrapper>
        ) : (
          <EmptyIndicator
            icon="users"
            size="small"
            title={messages.commonNoUsers}
          />
        )}
      </Box>

      <Pagination
        currentPage={pageValue}
        numberOfPages={data?.pages || 1}
        onPageChange={({ page }) => setPageValue(page)}
      />
    </div>
  );
};

export default CalendarEventParticipantsQueued;
